/**
 * Canonical EvoHome family of brands.
 *
 * Single source of truth for the four production websites and their names.
 * Every switcher, footer strip and family section must read from here so no
 * surface can drift to an old preview URL or an inconsistent brand name.
 */

export type EvoHomeFamilyKey = "improvements" | "maintenance" | "garden" | "property";

export interface EvoHomeFamilySite {
  key: EvoHomeFamilyKey;
  /** Public brand name, exactly as it must appear to homeowners. */
  name: string;
  /** Production website. Always https and always the www host. */
  url: string;
  /** One line explaining the brand. */
  description: string;
  /** True for this website (EvoHome Improvements). */
  isCurrent: boolean;
}

export const EVOHOME_FAMILY_SITES: EvoHomeFamilySite[] = [
  {
    key: "improvements",
    name: "EvoHome Improvements",
    url: "https://www.evohomeimprovements.co.uk",
    description: "Planning, quotations and oversight for home improvement projects.",
    isCurrent: true,
  },
  {
    key: "maintenance",
    name: "EvoHome Maintenance",
    url: "https://www.evohomemaintenance.co.uk",
    description: "Repairs, upkeep and aftercare for your home.",
    isCurrent: false,
  },
  {
    key: "garden",
    name: "EvoHome & Garden",
    url: "https://www.evohomeandgarden.co.uk",
    description: "Products and ideas for your home and garden.",
    isCurrent: false,
  },
  {
    key: "property",
    name: "EvoHome Property",
    url: "https://www.evohomeproperty.co.uk",
    description: "Selling, buying, letting and moving support.",
    isCurrent: false,
  },
];

/** Shown wherever we link to a sibling site, so nobody assumes one shared login. */
export const EVOHOME_FAMILY_SIGNIN_NOTE =
  "Each EvoHome website is separate for now, so you may need to sign in again. A shared sign-in is being introduced.";

export const EVOHOME_FAMILY_LABEL = "The EvoHome family";

/**
 * Public sister-site links: the footer "Part of the EvoHome family" strip and
 * the About page family cards. Sister brands stay visible either way.
 * Set this to true to make those sister names clickable links again.
 * EvoHome Improvements is unchanged. Signed-in switchers are unchanged.
 */
export const EVOHOME_FAMILY_SISTER_LINKS_ENABLED = false;

export function getFamilySite(key: EvoHomeFamilyKey): EvoHomeFamilySite {
  const site = EVOHOME_FAMILY_SITES.find((s) => s.key === key);
  if (!site) throw new Error(`Unknown EvoHome family site: ${key}`);
  return site;
}
